import React from "react";
import {
  BarChart as RechartsBarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

interface BarChartProps {
  data: Array<Record<string, string | number>>;
  dataKey: string;
  nameKey?: string;
  color?: string;
  isShowDollarSign?: boolean;
  /** Horizontal (bar chart with horizontal bars) for "top N" lists */
  horizontal?: boolean;
  /** Optional per-bar colors (e.g. [green, gray]). When provided, overrides color for horizontal bars. */
  colors?: string[];
  /** Suffix for value axis/tooltip (e.g. "%"). When set, value axis shows e.g. "95%" instead of "$95". */
  valueSuffix?: string;
  /** Category axis width for horizontal charts. Reduce on compact/mobile charts with short labels. */
  yAxisWidth?: number;
}

const DEFAULT_COLOR = "#82ca9d";
const TOP_BAR_DARKER = "#6ba882";

/** Left-align category labels inside the Y-axis width so short names do not leave a wide empty strip. */
function horizontalCategoryTick(props: Record<string, unknown>) {
  const payload = props.payload as { value: unknown };
  const index = props.index as number;
  const x = props.x as number;
  const y = props.y as number;
  const bandW = typeof props.width === "number" ? props.width : 160;
  const tickFormatter = props.tickFormatter as ((v: unknown, i: number) => string) | undefined;
  const raw = payload?.value;
  const label =
    tickFormatter?.(raw, index) ??
    (typeof raw === "string" ? raw : raw != null ? String(raw) : "");

  const labelX = x - bandW + 10;
  return (
    <text
      x={labelX}
      y={y}
      dy={4}
      textAnchor="start"
      fill="#4b5563"
      fontSize={11}
      className="recharts-cartesian-axis-tick-value"
    >
      {label}
    </text>
  );
}

export function ReportBarChart({
  data,
  dataKey,
  nameKey = "name",
  color = DEFAULT_COLOR,
  isShowDollarSign = true,
  horizontal = false,
  colors: colorsProp,
  valueSuffix,
  yAxisWidth = 168,
}: BarChartProps) {
  const valueFormatter = (value: number) =>
    valueSuffix ? `${Number(value).toFixed(1)}${valueSuffix}` : isShowDollarSign ? `$${Number(value).toFixed(2)}` : String(value);
  const tooltipFormatter = (value: number) =>
    [valueFormatter(value), horizontal ? "Share" : "Revenue"] as [string | number, string];

  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsBarChart
        data={data}
        layout={horizontal ? "vertical" : "horizontal"}
        margin={{ left: horizontal ? 14 : -15, bottom: 24, right: 20, top: 8 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={!horizontal} horizontal={horizontal} />
        {horizontal ? (
          <>
            <XAxis
              type="number"
              domain={valueSuffix === "%" ? [0, 100] : undefined}
              tickFormatter={(v) => (valueSuffix ? `${v}${valueSuffix}` : isShowDollarSign ? `$${v}` : String(v))}
              fontSize={11}
            />
            <YAxis
              type="category"
              dataKey={nameKey}
              width={yAxisWidth}
              tick={horizontalCategoryTick}
              tickFormatter={(v) =>
                typeof v === "string" && v.length > 32 ? `${v.slice(0, 29)}…` : v
              }
            />
          </>
        ) : (
          <>
            <XAxis dataKey={nameKey} tickLine={false} fontSize={11} padding={{ right: 35, left: 8 }} />
            <YAxis
              tickLine={false}
              tickFormatter={(v) => (isShowDollarSign ? `$${v}` : String(v))}
              fontSize={10}
            />
          </>
        )}
        <Tooltip
          formatter={tooltipFormatter}
          cursor={{ fill: "transparent" }}
          contentStyle={{
            fontSize: 12,
            padding: "8px 12px",
            borderRadius: 8,
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            border: "1px solid #e5e7eb",
          }}
          separator=": "
          labelStyle={{ marginBottom: 4 }}
        />
        <Bar dataKey={dataKey} fill={color} radius={[2, 2, 0, 0]} activeBar={false}>
          {horizontal && data.length > 0
            ? data.map((_, i) => (
                <Cell
                  key={i}
                  fill={colorsProp?.[i] ?? (i === 0 ? TOP_BAR_DARKER : color)}
                />
              ))
            : null}
        </Bar>
      </RechartsBarChart>
    </ResponsiveContainer>
  );
}
